import { NmLoader } from '../NmLoader';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { SlideUpOverlay } from '../SlideUpOverlay';
import { NmButton } from '../NmButton';
import { WorkoutNotificationSummary } from '../notifications/WorkoutNotificationSummary';
import '../../styles/notification-center.css';
import type { WorkoutDetail } from '../../services/api';
import { workouts } from '../../services/api';
import type { PeakTrendPoint } from '../../utils/coachPeakTrends';
import { workoutDetailToCalendarWorkout } from '../../utils/workoutDetailCalendar';

interface CoachPeakPointSheetProps {
  open: boolean;
  points: PeakTrendPoint[];
  index: number;
  unit: string;
  onClose: () => void;
  onIndexChange: (index: number) => void;
  onOpenAnalysis?: (workout: WorkoutDetail, userId: number) => void;
}

type WorkoutLoadState = {
  detail: WorkoutDetail | null;
  loading: boolean;
  error: boolean;
};

function formatPeakDate(ms: number, locale: string): string {
  const d = new Date(ms);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString(locale, {
    weekday: 'short',
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

function workoutKey(point: PeakTrendPoint): string {
  return `${point.recordId}:${point.workoutId ?? 'none'}`;
}

export function CoachPeakPointSheet({
  open,
  points,
  index,
  unit,
  onClose,
  onIndexChange,
  onOpenAnalysis,
}: CoachPeakPointSheetProps) {
  const { t, i18n } = useTranslation();
  const multiDay = points.length > 1;
  const point = points[index] ?? points[0];
  const total = points.length;
  const hasPrev = index > 0;
  const hasNext = index < total - 1;

  const [workoutByKey, setWorkoutByKey] = useState<Record<string, WorkoutLoadState>>({});

  const loadTargets = useMemo(
    () =>
      points
        .filter((p) => p.workoutId != null)
        .map((p) => ({ key: workoutKey(p), workoutId: p.workoutId as number })),
    [points],
  );

  useEffect(() => {
    if (!open) {
      setWorkoutByKey({});
      return;
    }

    if (!loadTargets.length) {
      setWorkoutByKey({});
      return;
    }

    let cancelled = false;
    const initial: Record<string, WorkoutLoadState> = {};
    for (const { key } of loadTargets) {
      initial[key] = { detail: null, loading: true, error: false };
    }
    setWorkoutByKey(initial);

    void Promise.all(
      loadTargets.map(async ({ key, workoutId }) => {
        try {
          const res = await workouts.getWorkoutDetail(workoutId);
          return { key, detail: res.data?.data?.workout ?? null, error: false };
        } catch {
          return { key, detail: null, error: true };
        }
      }),
    ).then((results) => {
      if (cancelled) return;
      setWorkoutByKey((prev) => {
        const next = { ...prev };
        for (const row of results) {
          next[row.key] = { detail: row.detail, loading: false, error: row.error };
        }
        return next;
      });
    });

    return () => {
      cancelled = true;
    };
  }, [open, loadTargets]);

  useEffect(() => {
    if (!open) return;
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') { event.preventDefault(); onClose(); }
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [open, onClose]);

  if (!point) return null;

  const renderWorkoutBlock = (p: PeakTrendPoint, showPeakValue: boolean) => {
    const key = workoutKey(p);
    const state = workoutByKey[key];
    const workoutDetail = state?.detail ?? null;
    const workoutLoading = state?.loading ?? !!p.workoutId;
    const workoutError = state?.error ?? false;
    const calendarWorkout = workoutDetail ? workoutDetailToCalendarWorkout(workoutDetail) : null;
    const canOpenAnalysis =
      workoutDetail != null &&
      workoutDetail.total_time != null &&
      Number(workoutDetail.total_time) > 0 &&
      !!onOpenAnalysis;

    return (
      <article key={p.recordId} className="coach-peak-point-sheet__entry">
        {showPeakValue && (
          <div className="coach-peak-point-sheet__entry-value ltr-island">
            <span className="coach-peak-point-sheet__value-num">{Math.round(p.y)}</span>
            <span className="coach-peak-point-sheet__value-unit">{unit}</span>
          </div>
        )}

        {p.workoutId == null && (
          <p className="coach-peak-point-sheet__entry-muted">
            {t('coach.peakTrends.noWorkoutLinked', 'No workout linked to this record.')}
          </p>
        )}

        {p.workoutId != null && workoutLoading && <NmLoader />}

        {p.workoutId != null && workoutError && !workoutLoading && (
          <p className="coach-peak-point-sheet__entry-muted">
            {t('coach.peakTrends.workoutLoadError', 'Could not load workout.')}
          </p>
        )}

        {calendarWorkout && !workoutLoading && (
          <div className="coach-peak-point-sheet__workout-card">
            <WorkoutNotificationSummary
              key={calendarWorkout.id}
              workout={calendarWorkout}
              rawWorkout={workoutDetail as unknown as Record<string, unknown>}
              onOpenWorkout={
                canOpenAnalysis ? () => onOpenAnalysis!(workoutDetail, p.athleteId) : undefined
              }
            />
          </div>
        )}
      </article>
    );
  };

  return (
    <SlideUpOverlay
      open={open}
      onClose={onClose}
      aria-label={t('coach.peakTrends.pointSheetTitle', 'Peak detail')}
      zIndex={260}
    >
      <div className="coach-peak-point-sheet">
        <div className="coach-peak-point-sheet__handle" aria-hidden />
        <div className="coach-peak-point-sheet__header">
          <button type="button" onClick={onClose} aria-label={t('common.close')} style={{ border: 0, background: 'transparent', color: 'inherit', cursor: 'pointer', padding: 8 }}>{t('common.close')}</button>
          <div className="coach-peak-point-sheet__header-main">
            <span className="coach-peak-point-sheet__dot" style={{ background: point.color }} />
            <strong className="coach-peak-point-sheet__athlete">{point.athleteName}</strong>
          </div>
          {!multiDay && total > 1 && (
            <span className="coach-peak-point-sheet__counter">
              {t('coach.peakTrends.pointCounter', '{{current}} of {{total}}', {
                current: index + 1,
                total,
              })}
            </span>
          )}
        </div>

        <p className="coach-peak-point-sheet__date">{formatPeakDate(point.x, i18n.language)}</p>

        {multiDay ? (
          <>
            <p className="coach-peak-point-sheet__multi-hint">
              {t('coach.peakTrends.sameDayPeaks', '{{count}} peaks on this day', {
                count: points.length,
              })}
            </p>
            <div className="coach-peak-point-sheet__entries">
              {points.map((p) => renderWorkoutBlock(p, true))}
            </div>
            {onOpenAnalysis && (
              <p className="coach-peak-point-sheet__entry-muted coach-peak-point-sheet__entry-muted--center">
                {t('coach.peakTrends.tapForAnalysis', 'Tap the workout to open full analysis')}
              </p>
            )}
          </>
        ) : (
          <>
            <div className="coach-peak-point-sheet__value ltr-island">
              <span className="coach-peak-point-sheet__value-num">{unit === 'W/kg' ? point.y.toFixed(1) : Math.round(point.y)}</span>
              <span className="coach-peak-point-sheet__value-unit">{unit}</span>
            </div>

            {renderWorkoutBlock(point, false)}

            {onOpenAnalysis &&
              workoutByKey[workoutKey(point)]?.detail &&
              !workoutByKey[workoutKey(point)]?.loading && (
                <p className="coach-peak-point-sheet__entry-muted coach-peak-point-sheet__entry-muted--center">
                  {t('coach.peakTrends.tapForAnalysis', 'Tap the workout to open full analysis')}
                </p>
              )}

            {total > 1 && (
              <div className="coach-peak-point-sheet__nav">
                <NmButton
                  variant="secondary"
                  size="sm"
                  disabled={!hasPrev}
                  onClick={() => onIndexChange(index - 1)}
                >
                  {t('coach.peakTrends.prevPeak', 'Previous')}
                </NmButton>
                <NmButton
                  variant="secondary"
                  size="sm"
                  disabled={!hasNext}
                  onClick={() => onIndexChange(index + 1)}
                >
                  {t('coach.peakTrends.nextPeak', 'Next')}
                </NmButton>
              </div>
            )}
          </>
        )}
      </div>
    </SlideUpOverlay>
  );
}
